import React, { ReactElement, useMemo, useState } from 'react'
import {
  Card, Table, Row, Col, Collapse, Button,
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
} from 'recharts'
import { IBroadcastFormRecipientResponseReportRow } from 'types/BroadcastForm'
import { calculateNPSScoreDetails, isNetPromoterQuestion } from 'utils/npsCalculation'
import { WordCloudChart } from './components/WordCloudChart'
import { NPSScoreCard } from './components/NPSScoreCard'

interface EnterpriseSurveyReportQuestionsProps {
  filteredRecipients: IBroadcastFormRecipientResponseReportRow[]
}

interface QuestionGroup {
  questionId: number
  questionText: string
  scaleType: string
  questionCategory: string
  responses: IBroadcastFormRecipientResponseReportRow[]
  totalResponses: number
  uniqueAnswers: Map<string, number>
}

const COLORS = [
  '#3b82f6', // blue-500
  '#10b981', // emerald-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#ec4899', // pink-500
  '#06b6d4', // cyan-500
  '#84cc16', // lime-500
  '#f97316', // orange-500
  '#6366f1', // indigo-500
  '#14b8a6', // teal-500
  '#dc2626', // red-600
]

// Scale types that should NOT show pie charts (e.g., open text)
const NO_CHART_SCALE_TYPES = [
  'Open Text',
]

// Custom tooltip for pie chart
const CustomPieTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload.length) {
    return null
  }

  const data = payload[0]
  return (
    <div
      className="bg-white border rounded shadow-lg p-3"
      style={{
        backgroundColor: 'white',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      <p className="font-weight-bold mb-2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
        {data.name}
      </p>
      <p style={{ margin: '4px 0', color: data.color }}>
        <span style={{ fontWeight: 'bold' }}>Count:</span>
        {' '}
        {data.value}
      </p>
      <p style={{ margin: '4px 0', color: data.color }}>
        <span style={{ fontWeight: 'bold' }}>Percentage:</span>
        {' '}
        {data.payload.percentage.toFixed(1)}
        %
      </p>
    </div>
  )
}

export const EnterpriseSurveyReportQuestions: React.FC<EnterpriseSurveyReportQuestionsProps> = ({
  filteredRecipients,
}): ReactElement => {
  // Track which questions are expanded (all collapsed by default)
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set())

  const toggleQuestion = (questionId: number) => {
    setExpandedQuestions((prev) => {
      const next = new Set(prev)
      if (next.has(questionId)) {
        next.delete(questionId)
      } else {
        next.add(questionId)
      }
      return next
    })
  }

  const questionGroups = useMemo(() => {
    // Filter to only responses with actual question data
    const questionResponses = filteredRecipients.filter(
      (r) => r.HasResponse && r.QuestionId != null && r.QuestionText,
    )

    // Group by question
    const questionMap = new Map<number, QuestionGroup>()

    questionResponses.forEach((response) => {
      const questionId = response.QuestionId
      if (!questionId) return

      if (!questionMap.has(questionId)) {
        questionMap.set(questionId, {
          questionId,
          questionText: response.QuestionText || `Question ${questionId}`,
          scaleType: response.ScaleType || 'Unknown',
          questionCategory: response.QuestionCategory || '',
          responses: [],
          totalResponses: 0,
          uniqueAnswers: new Map<string, number>(),
        })
      }

      const group = questionMap.get(questionId)
      if (!group) return

      group.responses.push(response)
      group.totalResponses += 1

      // Determine answer text for counting unique answers
      let answerText: string
      if (response.ResponseText != null && response.ResponseText !== '') {
        answerText = response.ResponseText
      } else if (response.OptionText != null && response.OptionText !== '') {
        answerText = response.OptionText
        if (response.OptionValue != null) {
          answerText = `${response.OptionText} (${response.OptionValue})`
        }
      } else if (response.OptionValue != null) {
        answerText = response.OptionValue.toString()
      } else {
        answerText = '(No answer)'
      }

      group.uniqueAnswers.set(answerText, (group.uniqueAnswers.get(answerText) || 0) + 1)
    })

    // Convert to array and sort by question text
    return Array.from(questionMap.values()).sort((a, b) => a.questionText.localeCompare(b.questionText))
  }, [filteredRecipients])

  if (questionGroups.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        No question data available to display.
      </div>
    )
  }

  return (
    <div className="mt-3">
      {questionGroups.map((group) => {
        // Sort unique answers by count (descending), then by text
        const sortedAnswers = Array.from(group.uniqueAnswers.entries())
          .sort((a, b) => {
            if (b[1] !== a[1]) {
              return b[1] - a[1] // Sort by count descending
            }
            return a[0].localeCompare(b[0]) // Then by text ascending
          })

        const isExpanded = expandedQuestions.has(group.questionId)

        return (
          <Card key={group.questionId} className="mb-4 shadow-sm">
            <Card.Header className="bg-light">
              <div className="d-flex align-items-start">
                <Button
                  variant="link"
                  className="p-0 mr-2 text-decoration-none"
                  onClick={() => toggleQuestion(group.questionId)}
                  aria-expanded={isExpanded}
                >
                  <FontAwesomeIcon
                    icon={isExpanded ? 'chevron-up' : 'chevron-down'}
                    className="text-muted"
                  />
                </Button>
                <div className="flex-grow-1">
                  <h5 className="mb-1">{group.questionText}</h5>
                  <div className="d-flex text-muted small">
                    <span className="mr-4">
                      <strong>Scale Type:</strong>
                      {' '}
                      {group.scaleType}
                    </span>
                    {group.questionCategory && (
                      <span className="mr-4">
                        <strong>Category:</strong>
                        {' '}
                        {group.questionCategory}
                      </span>
                    )}
                    <span>
                      <strong>Total Responses:</strong>
                      {' '}
                      {group.totalResponses}
                    </span>
                  </div>
                </div>
              </div>
            </Card.Header>
            <Collapse in={isExpanded}>
              <div>
                <Card.Body>
                  <Row>
                    {(() => {
                      // Check if this question has ResponseText responses (for word cloud)
                      const hasResponseText = group.responses.some(
                        (r) => r.ResponseText != null && r.ResponseText.trim() !== '',
                      )

                      // Check if this is a Net Promoter question
                      const isNPQ = isNetPromoterQuestion(group.scaleType, group.questionCategory)
                      const npsDetails = isNPQ ? calculateNPSScoreDetails(group.responses) : null

                      // Show word cloud for questions with ResponseText
                      if (hasResponseText) {
                        const responseTexts = group.responses
                          .filter((r) => r.ResponseText != null && r.ResponseText.trim() !== '')
                          .map((r) => r.ResponseText as string)

                        return (
                          <>
                            <Col md={4}>
                              {/* Show NPS Score Card for Net Promoter questions with text responses */}
                              {npsDetails && <NPSScoreCard npsDetails={npsDetails} />}
                              <div
                                className="bg-white border rounded shadow-sm p-3"
                                style={{
                                  backgroundColor: 'white',
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.25rem',
                                  boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)',
                                  padding: '1rem',
                                }}
                              >
                                <WordCloudChart responseTexts={responseTexts} height={300} />
                              </div>
                            </Col>
                            <Col md={8}>
                              <Table striped bordered hover size="sm">
                                <thead>
                                  <tr>
                                    <th style={{ width: '60%' }}>Answer</th>
                                    <th style={{ width: '20%' }} className="text-center">Count</th>
                                    <th style={{ width: '20%' }} className="text-center">Percentage</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sortedAnswers.map(([answerText, count]) => {
                                    const percentage = group.totalResponses > 0
                                      ? ((count / group.totalResponses) * 100).toFixed(1)
                                      : '0.0'

                                    return (
                                      <tr key={answerText}>
                                        <td>{answerText}</td>
                                        <td className="text-center">{count.toLocaleString()}</td>
                                        <td className="text-center">
                                          {percentage}
                                          %
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </Table>
                            </Col>
                          </>
                        )
                      }

                      // Show pie chart for other question types (if applicable)
                      if (!NO_CHART_SCALE_TYPES.includes(group.scaleType) && sortedAnswers.length > 0) {
                        return (
                          <>
                            <Col md={4}>
                              {/* Show NPS Score Card for Net Promoter questions above the pie chart */}
                              {npsDetails && <NPSScoreCard npsDetails={npsDetails} />}
                              <div
                                className="bg-white border rounded shadow-sm p-3"
                                style={{
                                  backgroundColor: 'white',
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.25rem',
                                  boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)',
                                  padding: '1rem',
                                }}
                              >
                                <ResponsiveContainer width="100%" height={300}>
                                  <PieChart>
                                    <Pie
                                      data={sortedAnswers.map(([answerText, count], index) => ({
                                        name: answerText.length > 30 ? `${answerText.substring(0, 30)}...` : answerText,
                                        value: count,
                                        percentage: group.totalResponses > 0 ? (count / group.totalResponses) * 100 : 0,
                                        fullName: answerText,
                                      }))}
                                      cx="50%"
                                      cy="50%"
                                      labelLine={false}
                                      label={({ name, percentage }) => `${name}: ${percentage.toFixed(1)}%`}
                                      outerRadius={80}
                                      fill="#8884d8"
                                      dataKey="value"
                                    >
                                      {sortedAnswers.map(([answerText], index) => (
                                        <Cell
                                          key={`cell-${group.questionId}-${answerText}`}
                                          fill={COLORS[index % COLORS.length]}
                                        />
                                      ))}
                                    </Pie>
                                    <Tooltip content={<CustomPieTooltip />} />
                                    <Legend
                                      wrapperStyle={{ fontSize: '12px' }}
                                      formatter={(value, entry: any) => entry.payload.fullName}
                                    />
                                  </PieChart>
                                </ResponsiveContainer>
                              </div>
                            </Col>
                            <Col md={8}>
                              <Table striped bordered hover size="sm">
                                <thead>
                                  <tr>
                                    <th style={{ width: '60%' }}>Answer</th>
                                    <th style={{ width: '20%' }} className="text-center">Count</th>
                                    <th style={{ width: '20%' }} className="text-center">Percentage</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sortedAnswers.map(([answerText, count]) => {
                                    const percentage = group.totalResponses > 0
                                      ? ((count / group.totalResponses) * 100).toFixed(1)
                                      : '0.0'

                                    return (
                                      <tr key={answerText}>
                                        <td>{answerText}</td>
                                        <td className="text-center">{count.toLocaleString()}</td>
                                        <td className="text-center">
                                          {percentage}
                                          %
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </Table>
                            </Col>
                          </>
                        )
                      }

                      // No chart, just table
                      return (
                        <Col md={12}>
                          {/* Show NPS Score Card for Net Promoter questions even without chart */}
                          {npsDetails && <NPSScoreCard npsDetails={npsDetails} />}
                          <Table striped bordered hover size="sm">
                            <thead>
                              <tr>
                                <th style={{ width: '60%' }}>Answer</th>
                                <th style={{ width: '20%' }} className="text-center">Count</th>
                                <th style={{ width: '20%' }} className="text-center">Percentage</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sortedAnswers.map(([answerText, count]) => {
                                const percentage = group.totalResponses > 0
                                  ? ((count / group.totalResponses) * 100).toFixed(1)
                                  : '0.0'

                                return (
                                  <tr key={answerText}>
                                    <td>{answerText}</td>
                                    <td className="text-center">{count.toLocaleString()}</td>
                                    <td className="text-center">
                                      {percentage}
                                      %
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </Table>
                        </Col>
                      )
                    })()}
                  </Row>
                </Card.Body>
              </div>
            </Collapse>
          </Card>
        )
      })}
    </div>
  )
}
